/**
 * Resolve and style the site logo for page header, media viewer, or slideshow.
 * Context-specific fileIds fall back to the page logo, then /logo.svg.
 */

import { cssBackdropFilter, cssFilter } from '../blocks/filter.js';
import { DEFAULT_BRAND_ID } from './header-blocks.js';

/**
 * @param {Object|null|undefined} logo - theme.brand.logo
 * @param {'page'|'media'|'slideshow'} [context='page']
 * @returns {string}
 */
export function resolveLogoSrc(logo, context = 'page') {
    if (context === 'media' && logo?.mediaFileId) return logo.mediaFileId;
    if (context === 'slideshow' && logo?.slideshowFileId) return logo.slideshowFileId;
    return logo?.fileId || logo?.src || '/logo.svg';
}

/**
 * Colour of a tinted logo. In the viewer it follows the controls colour,
 * which falls back to the page text outside it.
 * @param {'page'|'media'|'slideshow'} [context='page']
 */
export function logoTint(context = 'page') {
    return context === 'page' ? 'var(--color-text)' : 'var(--viewer-controls, var(--color-text))';
}

/**
 * How the logo is drawn.
 *
 * 'mask' is the long-standing behaviour and stays the default: the SVG is used as
 * a mask filled with the text colour, so the logo always matches the page text.
 * A published theme can switch to 'image' (original colours) or 'text' (a
 * wordmark), but an un-themed site must keep getting the mask.
 *
 * @param {Object|null|undefined} logo - theme.brand.logo
 * @param {'page'|'media'|'slideshow'} [context='page']
 * @returns {Object} Inline style for the logo element
 */
export function buildLogoStyle(logo, context = 'page') {
    const src = resolveLogoSrc(logo, context);
    const tint = logoTint(context);

    if (logo?.mode === 'image') {
        return {
            backgroundImage: `url("${src}")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'left center',
        };
    }

    if (logo?.mode === 'text') {
        return {
            color: tint,
            // Brand faceId dual-writes fonts.wordmark → --font-display (see header-blocks).
            fontFamily: 'var(--font-display, "Sansumi", system-ui, sans-serif)',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
        };
    }

    // Mask (default) — same tint semantics as Vector inheritTextColour.
    return {
        backgroundColor: tint,
        maskImage: `url("${src}")`,
        maskSize: 'contain',
        maskRepeat: 'no-repeat',
        maskPosition: 'left center',
        WebkitMaskImage: `url("${src}")`,
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'left center',
    };
}

/**
 * CSS filter for a text wordmark. Invert-behind uses difference blending so
 * the glyphs flip the page, the same way fullscreen text does.
 * @param {Object|null|undefined} filter
 * @returns {Object}
 */
export function logoFilterStyle(filter) {
    const css = cssFilter(filter);
    const backdrop = cssBackdropFilter(filter);
    if (!css && !backdrop) return {};
    if (backdrop) {
        return {
            mixBlendMode: 'difference',
            color: '#fff',
            ...(css ? { filter: css } : {}),
        };
    }
    return { filter: css };
}

/**
 * Page the logo should open.
 * A header brand block wins, then the theme logo, then the legacy
 * logo_link_page_id property. A null theme value must not discard that property.
 * @param {Object|null|undefined} block
 * @param {Object|null|undefined} siteLogo
 * @param {Object|null|undefined} properties
 * @returns {string|null}
 */
export function resolveLogoHref(block, siteLogo, properties) {
    const fromBlock = block?.props?.hrefPageId;
    if (fromBlock) return fromBlock;
    if (siteLogo?.hrefPageId) return siteLogo.hrefPageId;
    return properties?.logo_link_page_id || null;
}

/**
 * Filter painted on a header brand. The default brand shares brand.logo.filter
 * with the viewer and slideshow marks. Any other brand block can override it.
 * @param {Object|null|undefined} block
 * @param {Object|null|undefined} siteLogo
 */
export function resolveBrandFilter(block, siteLogo) {
    const props = block?.props || {};
    if (block?.deviceChanges && 'filter' in block.deviceChanges) return block.deviceChanges.filter;
    if (!block || block.id === DEFAULT_BRAND_ID) return siteLogo?.filter ?? props.filter ?? null;
    if (props.filter !== undefined) return props.filter;
    return siteLogo?.filter ?? null;
}

/**
 * Brand header block → logo style. Block props override site brand.logo;
 * null fileId inherits the site page logo asset.
 *
 * @param {Object|null|undefined} brandProps - header brand block.props
 * @param {Object|null|undefined} siteLogo - theme.brand.logo
 * @returns {Object} Inline style
 */
export function buildBrandLogoStyle(brandProps, siteLogo) {
    const mode = brandProps?.mode || siteLogo?.mode || 'mask';
    const fileId = brandProps?.fileId != null && brandProps.fileId !== ''
        ? brandProps.fileId
        : null;
    const merged = {
        ...(siteLogo || {}),
        mode,
        fileId: fileId ?? siteLogo?.fileId ?? null,
        text: brandProps?.text || siteLogo?.text || '',
    };
    return buildLogoStyle(merged, 'page');
}

/**
 * True when the wordmark HTML/plain text has author line breaks (Enter),
 * not just a single wrapping block from contentEditable.
 * @param {string|null|undefined} html
 * @param {string|null|undefined} [plain]
 */
export function wordmarkHasManualBreaks(html, plain) {
    const source = String(html || '');
    if (/<br\s*\/?>/i.test(source)) return true;
    const asText = source
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(div|p|li|h[1-6])>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\u00a0/g, ' ');
    const fromHtml = asText.split(/\n/).map((line) => line.trim()).filter(Boolean);
    if (fromHtml.length > 1) return true;
    const fromPlain = String(plain || '')
        .split(/\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    return fromPlain.length > 1;
}

/**
 * Intrinsic one-line width of the wordmark at its current font size.
 * Must not use the laid-out 100%-wide box — that makes scrollWidth === clientWidth
 * when the type is too small, so phone→desktop can never grow.
 * @param {HTMLElement} fillEl
 */
function wordmarkNaturalWidth(fillEl) {
    const prevWidth = fillEl.style.width;
    const prevMaxWidth = fillEl.style.maxWidth;
    fillEl.style.width = 'max-content';
    fillEl.style.maxWidth = 'none';
    const natural = fillEl.scrollWidth;
    fillEl.style.width = prevWidth;
    fillEl.style.maxWidth = prevMaxWidth;
    return natural;
}

/**
 * Scale a wordmark so one line spans the module width edge-to-edge, and set the
 * module height to that type size so the selection outline hugs the glyphs.
 * Scales from the current computed size (does not clear font-size first) so a
 * ResizeObserver watching the module is not kicked into a feedback loop.
 * @param {HTMLElement|null|undefined} moduleEl
 * @param {HTMLElement|null|undefined} fillEl
 * @returns {boolean} true when the inline font-size changed
 */
export function fitWordmarkToWidth(moduleEl, fillEl) {
    if (!moduleEl || !fillEl) return false;
    // A resize drag owns the box. Fitting now would chase the live width.
    if (moduleEl.dataset?.attachDragging === '1') return false;
    fillEl.style.whiteSpace = 'nowrap';
    fillEl.style.lineHeight = '1';
    const available = Math.round(moduleEl.clientWidth);
    if (!(available > 0)) return false;

    // Same width as last successful fit — skip to avoid RO → height → RO loops.
    if (
        fillEl.dataset.fitWidthPx === String(available)
        && fillEl.style.fontSize
    ) {
        return false;
    }

    const current = parseFloat(getComputedStyle(fillEl).fontSize);
    if (!(current > 0)) return false;
    const natural = wordmarkNaturalWidth(fillEl);
    if (!(natural > 0)) return false;
    const next = current * (available / natural);
    if (!Number.isFinite(next) || next <= 0) return false;

    // Sub-pixel thrash still delivers RO notifications in Chromium.
    if (fillEl.style.fontSize && Math.abs(next - current) < 0.5) {
        fillEl.dataset.fitWidthPx = String(available);
        const tight = Math.ceil(next);
        if (Math.abs((parseFloat(moduleEl.style.height) || 0) - tight) >= 0.5) {
            moduleEl.style.height = `${tight}px`;
        }
        return false;
    }

    fillEl.style.fontSize = `${next}px`;
    fillEl.dataset.fitWidthPx = String(available);
    // Height tracks type size (line-height 1) so the bbox stays perfect.
    // Font size stays on the fill: setting it on the module changes an em
    // width, and the fit then chases itself.
    moduleEl.style.height = `${Math.ceil(next)}px`;
    return true;
}

/**
 * Source for a Vector block. null fileId inherits the brand page logo.
 * Independent of brand.logo.mode — tinting is inheritTextColour on the block.
 *
 * @param {Object|null|undefined} block - vector block
 * @param {Object|null|undefined} logo - theme.brand.logo
 * @returns {string}
 */
export function resolveVectorSrc(block, logo) {
    if (block?.fileId) return block.fileId;
    return resolveLogoSrc(logo, 'page');
}

/**
 * CSS width from a vector block's width field ({ value, unit } or number/string).
 * @param {Object|number|string|null|undefined} width
 * @returns {string}
 */
export function vectorWidthCss(width) {
    if (width == null || width === '') return '80vw';
    if (typeof width === 'object') {
        const value = Number(width.value);
        const unit = width.unit || 'vw';
        if (!Number.isFinite(value)) return '80vw';
        return `${value}${unit}`;
    }
    if (typeof width === 'number') return `${width}vw`;
    return String(width);
}

/**
 * Styles for a Vector block.
 *
 * Height defaults to auto so the rendered graphic can follow the asset's
 * intrinsic aspect ratio (see VectorBlock). Tint mode returns mask styles for
 * the paint layer; original colours leave painting to an <img>.
 *
 * @param {Object} block
 * @param {Object|null|undefined} logo
 * @returns {{ src: string, width: string, height: string, tint: boolean, style: Object }}
 */
export function buildVectorStyle(block, logo) {
    const src = resolveVectorSrc(block, logo);
    const width = vectorWidthCss(block?.width);
    const height = block?.height && block.height !== 'auto' ? String(block.height) : 'auto';
    const tint = block?.inheritTextColour !== false;
    const box = {
        width,
        height,
        // No max-width/max-height — authors may size past the viewport; the
        // fullscreen overlay clips overflow without creating scrollbars.
        display: 'inline-block',
        flexShrink: 0,
    };

    if (!tint) {
        return {
            src,
            width,
            height,
            tint: false,
            style: box,
        };
    }

    return {
        src,
        width,
        height,
        tint: true,
        style: {
            ...box,
            backgroundColor: 'var(--color-text)',
            maskImage: `url("${src}")`,
            maskSize: '100% 100%',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskImage: `url("${src}")`,
            WebkitMaskSize: '100% 100%',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
        },
    };
}

export default {
    resolveLogoSrc,
    buildLogoStyle,
    logoFilterStyle,
    resolveLogoHref,
    resolveBrandFilter,
    buildBrandLogoStyle,
    wordmarkHasManualBreaks,
    fitWordmarkToWidth,
    resolveVectorSrc,
    buildVectorStyle,
    vectorWidthCss,
};
